(() => {
  window.RS = window.RS || {};

  function debounce(fn, wait) {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, args), wait);
    };
  }

  // ---------------------------------------------------------------------------
  // SearchForm — base web component (plain submit + reset handling)
  // ---------------------------------------------------------------------------
  class SearchForm extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input[type="search"]');
      this.resetButton = this.querySelector('button[type="reset"]');
      if (!this.input) return;

      this.input.form.addEventListener('reset', this.onFormReset.bind(this));
      this.input.addEventListener('input', debounce(this.onChange.bind(this), 300));

      const searchBtn = this.querySelector('button.search-btn');
      if (searchBtn) {
        searchBtn.addEventListener('click', () => {
          if (this.input.value.trim().length) {
            this.input.form.requestSubmit();
          } else {
            this.input.focus();
          }
        });
      }
    }

    toggleResetButton() {
      if (!this.resetButton) return;
      const isHidden = this.resetButton.classList.contains('hidden');
      if (this.input.value.length > 0 && isHidden) {
        this.resetButton.classList.remove('hidden');
      } else if (this.input.value.length === 0 && !isHidden) {
        this.resetButton.classList.add('hidden');
      }
    }

    onChange() {
      this.toggleResetButton();
    }

    shouldResetForm() {
      return !document.querySelector('[aria-selected="true"] a');
    }

    onFormReset(event) {
      event.preventDefault();
      if (this.shouldResetForm()) {
        this.input.value = '';
        this.input.focus();
        this.toggleResetButton();
      }
    }
  }

  // ---------------------------------------------------------------------------
  // PredictiveSearch — extends SearchForm, adds live dropdown via S&D API
  // ---------------------------------------------------------------------------
  class PredictiveSearch extends SearchForm {
    connectedCallback() {
      super.connectedCallback();
      if (!this.input) return;

      // data-* selectors for JS targeting — classes are CSS-only
      this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
      this.isOpen = false;
      this.abortController = new AbortController();
      this.searchTerm = '';
      this.cachedResults = {};

      if (!this.predictiveSearchResults) return;

      this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));
      this.input.addEventListener('focus', this.onFocus.bind(this));
      this.addEventListener('focusout', this.onFocusOut.bind(this));

      const searchBtn = this.querySelector('button.search-btn');
      if (searchBtn) {
        searchBtn.addEventListener('click', () => {
          if (this.getQuery().length) {
            this.input.form.requestSubmit();
          } else {
            this.input.focus();
          }
        });
      }
      this.addEventListener('keyup', this.onKeyup.bind(this));
      this.addEventListener('keydown', this.onKeydown.bind(this));
    }

    getQuery() {
      return this.input.value.trim();
    }

    onChange() {
      super.onChange();
      const newSearchTerm = this.getQuery();
      if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
        this.querySelector('[data-predictive-search-results-wrapper]')?.remove();
      }
      this.updateSearchForTerm(this.searchTerm, newSearchTerm);
      this.searchTerm = newSearchTerm;
      if (!this.searchTerm.length) {
        this.close(true);
        return;
      }
      this.getSearchResults(this.searchTerm);
    }

    onFormSubmit(event) {
      if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) {
        event.preventDefault();
      }
    }

    onFormReset(event) {
      super.onFormReset(event);
      if (super.shouldResetForm()) {
        this.searchTerm = '';
        this.abortController.abort();
        this.abortController = new AbortController();
        this.closeResults(true);
      }
    }

    onFocus() {
      const currentSearchTerm = this.getQuery();
      if (!currentSearchTerm.length) return;
      if (this.searchTerm !== currentSearchTerm) {
        this.onChange();
      } else if (this.getAttribute('results') === 'true') {
        this.open();
      } else {
        this.getSearchResults(this.searchTerm);
      }
    }

    onFocusOut() {
      setTimeout(() => {
        if (!this.contains(document.activeElement)) this.close();
      });
    }

    onKeyup(event) {
      if (!this.getQuery().length) this.close(true);
      event.preventDefault();
      switch (event.code) {
        case 'ArrowUp':   this.switchOption('up');   break;
        case 'ArrowDown': this.switchOption('down'); break;
        case 'Enter':     this.selectOption();       break;
      }
    }

    onKeydown(event) {
      if (event.code === 'ArrowUp' || event.code === 'ArrowDown') event.preventDefault();
    }

    updateSearchForTerm(previousTerm, newTerm) {
      const el = this.querySelector('[data-predictive-search-search-for-text]');
      if (!el?.innerText) return;
      if (el.innerText.match(new RegExp(previousTerm, 'g')).length > 1) return;
      el.innerText = el.innerText.replace(previousTerm, newTerm);
    }

    switchOption(direction) {
      if (!this.getAttribute('open')) return;
      const moveUp = direction === 'up';
      const selectedElement = this.querySelector('[aria-selected="true"]');
      // data-predictive-search-item targets keyboard-navigable results
      const allVisibleElements = Array.from(
        this.querySelectorAll('li, button[data-predictive-search-item]')
      ).filter((el) => el.offsetParent !== null);

      if (moveUp && !selectedElement) return;

      let selectedElementIndex = -1;
      let i = 0;
      while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
        if (allVisibleElements[i] === selectedElement) selectedElementIndex = i;
        i++;
      }

      const statusEl = this.querySelector('[data-predictive-search-status]');
      if (statusEl) statusEl.textContent = '';

      let activeElementIndex = 0;
      if (!moveUp && selectedElement) {
        activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
      } else if (moveUp) {
        activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
      }
      if (activeElementIndex === selectedElementIndex) return;

      const activeElement = allVisibleElements[activeElementIndex];
      if (!activeElement) return;
      activeElement.setAttribute('aria-selected', true);
      if (selectedElement) selectedElement.setAttribute('aria-selected', false);
      this.input.setAttribute('aria-activedescendant', activeElement.id);
    }

    selectOption() {
      const selectedOption = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');
      if (selectedOption) selectedOption.click();
    }

    getSearchResults(searchTerm) {
      const queryKey = searchTerm.replace(' ', '-').toLowerCase();
      this.setLiveRegionLoadingState();
      if (this.cachedResults[queryKey]) {
        this.renderSearchResults(this.cachedResults[queryKey]);
        return;
      }
      fetch(
        `${routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&section_id=rs-predictive-search&options%5Bprefix%5D=none`,
        { signal: this.abortController.signal }
      )
        .then((response) => {
          if (!response.ok) {
            this.close();
            throw new Error(response.status);
          }
          return response.text();
        })
        .then((text) => {
          const sectionEl = new DOMParser()
            .parseFromString(text, 'text/html')
            .querySelector('#shopify-section-rs-predictive-search');
          if (!sectionEl) return;
          const resultsMarkup = sectionEl.innerHTML;
          document.querySelectorAll('predictive-search').forEach((instance) => {
            instance.cachedResults[queryKey] = resultsMarkup;
          });
          this.renderSearchResults(resultsMarkup);
        })
        .catch((error) => {
          if (error?.code === 20) return;
          this.close();
          throw error;
        });
    }

    setLiveRegionLoadingState() {
      this.statusElement = this.statusElement || this.querySelector('[data-predictive-search-status]');
      this.loadingText = this.loadingText || this.getAttribute('data-loading-text');
      if (!this.statusElement) return;
      this.setLiveRegionText(this.loadingText);
      this.setAttribute('loading', true);
    }

    setLiveRegionText(statusText) {
      if (!this.statusElement) return;
      this.statusElement.setAttribute('aria-hidden', 'false');
      this.statusElement.textContent = statusText;
      setTimeout(() => {
        this.statusElement?.setAttribute('aria-hidden', 'true');
      }, 1000);
    }

    renderSearchResults(resultsMarkup) {
      this.predictiveSearchResults.innerHTML = resultsMarkup;
      this.setAttribute('results', true);
      this.setLiveRegionResults();
      this.applyViewRouting();
      this.open();
    }

    applyViewRouting() {
      const view = new URLSearchParams(window.location.search).get('view');
      if (!view) return;
      this.querySelectorAll('[data-predictive-search] a[href*="/search"]').forEach(link => {
        const url = new URL(link.href);
        url.searchParams.set('view', view);
        link.href = url.toString();
      });
    }

    setLiveRegionResults() {
      this.removeAttribute('loading');
      const countEl = this.querySelector('[data-predictive-search-live-region-count-value]');
      if (countEl) this.setLiveRegionText(countEl.textContent);
    }

    getResultsMaxHeight() {
      const header = document.querySelector('#shopify-section-header');
      this.resultsMaxHeight = Math.min(window.innerHeight - (header?.getBoundingClientRect().bottom || 0), 480);
      return this.resultsMaxHeight;
    }

    open() {
      if (!this.predictiveSearchResults) return;
      this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight
        ? `${this.resultsMaxHeight}px`
        : `${this.getResultsMaxHeight()}px`;
      this.setAttribute('open', true);
      this.input.setAttribute('aria-expanded', true);
      this.isOpen = true;
    }

    close(clearSearchTerm = false) {
      this.closeResults(clearSearchTerm);
      this.isOpen = false;
    }

    closeResults(clearSearchTerm = false) {
      if (clearSearchTerm) {
        this.input.value = '';
        this.removeAttribute('results');
      }
      const selected = this.querySelector('[aria-selected="true"]');
      if (selected) selected.setAttribute('aria-selected', false);
      this.input.setAttribute('aria-activedescendant', '');
      this.removeAttribute('loading');
      this.removeAttribute('open');
      this.input.setAttribute('aria-expanded', false);
      this.resultsMaxHeight = false;
      if (this.predictiveSearchResults) this.predictiveSearchResults.removeAttribute('style');
    }
  }

  customElements.get('search-form') || customElements.define('search-form', SearchForm);
  customElements.get('predictive-search') || customElements.define('predictive-search', PredictiveSearch);

  if (!customElements.get('search-clear')) {
    class SearchClear extends HTMLElement {
      connectedCallback() {
        this._input = this.closest('form')?.querySelector('input[type="search"]');
        if (!this._input) return;

        this._sync = () => this.toggleAttribute('hidden', this._input.value === '');
        this._clear = () => {
          this._input.value = '';
          this._input.dispatchEvent(new Event('input', { bubbles: true }));
          this._input.focus();
        };

        this._input.addEventListener('input', this._sync);
        this._input.addEventListener('focus', this._sync);
        this.addEventListener('click', this._clear);
        this.addEventListener('keydown', (e) => { if (e.key === 'Enter' || e.key === ' ') this._clear(); });
        this._sync();
      }

      disconnectedCallback() {
        if (!this._input) return;
        this._input.removeEventListener('input', this._sync);
        this._input.removeEventListener('focus', this._sync);
      }
    }
    customElements.define('search-clear', SearchClear);
  }

  window.RS.SearchForm = SearchForm;
  window.RS.PredictiveSearch = PredictiveSearch;
})();
